// Motion helpers (Motion Build Plan). Presentation only: nothing here touches data or requests.
import { useEffect, useRef, useState } from 'react'

/** True when the iPad has Reduce Motion on: every animation then shows its end state at once. */
export function reduced(): boolean {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches } catch { return false }
}

/** Ease-out cubic, the curve every count-up uses. */
const easeOut = (k: number) => 1 - Math.pow(1 - k, 3)

/** A number that rolls to its new value over `ms` (250 ms by default) instead of jumping. Renders the target at once
 *  on first mount and under Reduce Motion. Use it for totals, counts and amounts on buttons; never for anything a
 *  decision reads (the underlying value is unchanged, only what is drawn). */
export function useCountUp(value: number, ms = 250): number {
  const [shown, setShown] = useState(value)
  const from = useRef(value)
  const raf = useRef(0)
  useEffect(() => {
    cancelAnimationFrame(raf.current)
    if (reduced() || from.current === value) { from.current = value; setShown(value); return }
    const start = from.current, t0 = performance.now()
    const tick = (now: number) => {
      const k = Math.min(1, (now - t0) / ms)
      const v = start + (value - start) * easeOut(k)
      setShown(k < 1 ? v : value)
      if (k < 1) raf.current = requestAnimationFrame(tick); else from.current = value
    }
    raf.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf.current)
  }, [value, ms])
  return shown
}

/** Restart a one-shot CSS animation class on an element (remove, reflow, add). */
export function replay(el: Element | null, cls: string) {
  if (!el || reduced()) return
  el.classList.remove(cls)
  void (el as HTMLElement).offsetWidth
  el.classList.add(cls)
}

/** Shake an element once (a wrong ID, a refused sheet, a drawer that is short). The content stays where it is. */
export function shake(el: Element | null) { replay(el, 'm-shake') }

/** A key that changes every time `trigger` becomes true, so a `key=` on an element restarts its entrance. */
export function useReplayKey(trigger: unknown): number {
  const [k, setK] = useState(0)
  const prev = useRef(trigger)
  useEffect(() => { if (trigger && trigger !== prev.current) setK((x) => x + 1); prev.current = trigger }, [trigger])
  return k
}
